import { useState } from 'react';
import { Card, Text, Button, Input, Badge } from '@fluentui/react-components';
import { useAuth } from '@/auth/AuthContext';
import './AdminUsers.css';

interface AdminUserItem {
  id: string;
  email: string;
  name: string;
  role: 'owner' | 'admin' | 'viewer';
  createdAt: string;
  lastLogin?: string;
  isActive: boolean;
}

const DEMO_ADMINS: AdminUserItem[] = [
  {
    id: 'admin-1',
    email: 'owner@sherpai.com',
    name: 'Sarah Johnson',
    role: 'owner',
    createdAt: '2025-01-01T00:00:00Z',
    lastLogin: '2026-04-04T09:30:00Z',
    isActive: true,
  },
  {
    id: 'admin-2',
    email: 'john@sherpai.com',
    name: 'John Smith',
    role: 'admin',
    createdAt: '2025-02-15T00:00:00Z',
    lastLogin: '2026-04-03T14:15:00Z',
    isActive: true,
  },
  {
    id: 'admin-3',
    email: 'maria@sherpai.com',
    name: 'Maria Garcia',
    role: 'admin',
    createdAt: '2025-03-10T00:00:00Z',
    lastLogin: '2026-04-02T11:45:00Z',
    isActive: true,
  },
  {
    id: 'admin-4',
    email: 'viewer@sherpai.com',
    name: 'Robert Lee',
    role: 'viewer',
    createdAt: '2025-03-20T00:00:00Z',
    lastLogin: '2026-03-30T16:20:00Z',
    isActive: true,
  },
];

export default function AdminUsersPage() {
  const { user } = useAuth();
  const [admins] = useState<AdminUserItem[]>(DEMO_ADMINS);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState<'all' | 'owner' | 'admin' | 'viewer'>('all');

  const filteredAdmins = admins.filter((admin) => {
    const matchesSearch =
      admin.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      admin.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = selectedRole === 'all' || admin.role === selectedRole;
    return matchesSearch && matchesRole;
  });

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'success';
      case 'admin':
        return 'warning';
      case 'viewer':
        return 'subtle';
      default:
        return 'subtle';
    }
  };

  return (
    <div className="adminusers-container">
      <div className="page-header">
        <div>
          <h1>Admin Users</h1>
          <Text size={300}>Manage admin accounts and permissions</Text>
        </div>
        {user?.role === 'owner' && (
          <Button
            appearance="primary"
            onClick={() => alert('Invite admin functionality coming soon')}
          >
            + Invite Admin
          </Button>
        )}
      </div>

      <Card className="filters-card">
        <div className="filters-grid">
          <Input
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearchQuery(e.target.value)
            }
            style={{ flex: 1 }}
          />
          <div className="role-filter">
            <select
              value={selectedRole}
              onChange={(e) =>
                setSelectedRole(
                  e.target.value as 'all' | 'owner' | 'admin' | 'viewer'
                )
              }
              className="role-select"
            >
              <option value="all">All Roles</option>
              <option value="owner">Owner</option>
              <option value="admin">Admin</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>
        </div>
      </Card>

      <div className="admins-table-container">
        <table className="admins-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last Login</th>
              <th>Created</th>
              {user?.role === 'owner' && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filteredAdmins.map((admin) => (
              <tr key={admin.id}>
                <td>
                  <Text weight="semibold">{admin.name}</Text>
                </td>
                <td>
                  <Text>{admin.email}</Text>
                </td>
                <td>
                  <Badge appearance="outline" color={getRoleColor(admin.role)}>
                    {admin.role.charAt(0).toUpperCase() + admin.role.slice(1)}
                  </Badge>
                </td>
                <td>
                  <Badge
                    appearance="outline"
                    color={admin.isActive ? 'success' : 'danger'}
                  >
                    {admin.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </td>
                <td>
                  <Text>
                    {admin.lastLogin
                      ? new Date(admin.lastLogin).toLocaleDateString()
                      : 'Never'}
                  </Text>
                </td>
                <td>
                  <Text>
                    {new Date(admin.createdAt).toLocaleDateString()}
                  </Text>
                </td>
                {user?.role === 'owner' && (
                  <td>
                    <Button
                      size="small"
                      appearance="subtle"
                      onClick={() =>
                        alert(`Edit admin ${admin.name} functionality coming soon`)
                      }
                    >
                      Edit
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Card className="stats-card">
        <h3>Admin Statistics</h3>
        <div className="stats-grid">
          <div className="stat-item">
            <Text size={400} weight="bold" className="stat-value">
              {admins.length}
            </Text>
            <Text size={200}>Total Admins</Text>
          </div>
          <div className="stat-item">
            <Text size={400} weight="bold" className="stat-value">
              {admins.filter((a) => a.role === 'owner').length}
            </Text>
            <Text size={200}>Owners</Text>
          </div>
          <div className="stat-item">
            <Text size={400} weight="bold" className="stat-value">
              {admins.filter((a) => a.role === 'admin').length}
            </Text>
            <Text size={200}>Admins</Text>
          </div>
          <div className="stat-item">
            <Text size={400} weight="bold" className="stat-value">
              {admins.filter((a) => a.role === 'viewer').length}
            </Text>
            <Text size={200}>Viewers</Text>
          </div>
        </div>
      </Card>
    </div>
  );
}
